import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ArrowLeft, Clock, Zap, ChefHat, X } from "lucide-react";
import axios from "axios";
import Navigation from "@/components/Navigation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RecipesPage = () => {
  const [recipes, setRecipes] = useState([]);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecipes();
  }, []);

  const fetchRecipes = async () => {
    try {
      const response = await axios.get(`${API}/recipes`);
      setRecipes(response.data);
    } catch (error) {
      console.error("Error fetching recipes:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navigation />
      
      <section className="pt-28 pb-24 px-6 lg:px-12" data-testid="recipes-section">
        <div className="container mx-auto max-w-6xl">
          {/* Back Link */}
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-[#4FB3D4] hover:text-[#4FB3D4]/80 mb-8">
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Link>

          {/* Header */}
          <div className="mb-12">
            <p className="text-sm font-medium uppercase tracking-widest text-[#FFB7B2] mb-4">
              Nourish Your Body
            </p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#1A2A3A] mb-4">
              Health & Energy Recipes
            </h1>
            <p className="text-lg text-[#1A2A3A]/60 max-w-2xl">
              Discover recipes designed to boost your energy, support your emotional wellness, and nourish your healing journey.
            </p>
          </div>

          {/* Recipe Grid */}
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="rounded-2xl overflow-hidden animate-pulse">
                  <div className="h-48 bg-[#E5E7EB]"></div>
                  <CardContent className="p-6">
                    <div className="h-6 bg-[#E5E7EB] rounded w-3/4 mb-4"></div>
                    <div className="h-4 bg-[#E5E7EB] rounded w-full mb-2"></div>
                    <div className="h-4 bg-[#E5E7EB] rounded w-2/3"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {recipes.map((recipe, index) => (
                <Dialog key={recipe.id}>
                  <DialogTrigger asChild>
                    <Card 
                      className="recipe-card cursor-pointer"
                      data-testid={`recipe-card-${index}`}
                      onClick={() => setSelectedRecipe(recipe)}
                    >
                      <div className="h-48 overflow-hidden">
                        <img 
                          src={recipe.image_url || "https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg"}
                          alt={recipe.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <CardContent className="p-6">
                        <h3 className="font-heading text-xl font-semibold text-[#1A2A3A] mb-2">
                          {recipe.title}
                        </h3>
                        <p className="text-[#1A2A3A]/60 text-sm mb-4 line-clamp-2">
                          {recipe.description}
                        </p>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="flex items-center gap-1 text-[#4FB3D4]">
                            <Clock className="h-4 w-4" />
                            {recipe.prep_time}
                          </span>
                          <span className="flex items-center gap-1 text-[#FFB7B2]">
                            <Zap className="h-4 w-4" />
                            Energy Boost
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </DialogTrigger>
                  
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl" data-testid="recipe-modal">
                    <DialogHeader>
                      <DialogTitle className="font-heading text-2xl text-[#1A2A3A] pr-8">
                        {recipe.title}
                      </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-6">
                      {/* Recipe Image */}
                      <div className="h-64 rounded-xl overflow-hidden">
                        <img 
                          src={recipe.image_url || "https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg"}
                          alt={recipe.title}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      {/* Meta */}
                      <div className="flex flex-wrap gap-4">
                        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#4FB3D4]/10 text-[#4FB3D4] text-sm">
                          <Clock className="h-4 w-4" />
                          {recipe.prep_time}
                        </span>
                        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFB7B2]/20 text-[#FFB7B2] text-sm">
                          <Zap className="h-4 w-4" />
                          Energy Boost
                        </span>
                      </div>

                      {/* Description */}
                      <p className="text-[#1A2A3A]/70">{recipe.description}</p>

                      {/* Energy Benefit */}
                      <div className="p-4 bg-gradient-to-br from-[#4FB3D4]/10 to-[#DCD6F7]/10 rounded-xl">
                        <h4 className="font-semibold text-[#1A2A3A] mb-2 flex items-center gap-2">
                          <Zap className="h-5 w-5 text-[#FFB7B2]" />
                          Energy Benefit
                        </h4>
                        <p className="text-[#1A2A3A]/60 text-sm">{recipe.energy_benefit}</p>
                      </div>

                      {/* Ingredients */}
                      <div>
                        <h4 className="font-semibold text-[#1A2A3A] mb-3 flex items-center gap-2">
                          <ChefHat className="h-5 w-5 text-[#4FB3D4]" />
                          Ingredients
                        </h4>
                        <ul className="grid grid-cols-2 gap-2">
                          {recipe.ingredients.map((ingredient, idx) => (
                            <li key={idx} className="flex items-center gap-2 text-[#1A2A3A]/70 text-sm">
                              <span className="w-2 h-2 rounded-full bg-[#4FB3D4]"></span>
                              {ingredient}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Instructions */}
                      <div>
                        <h4 className="font-semibold text-[#1A2A3A] mb-3">Instructions</h4>
                        <ol className="space-y-3">
                          {recipe.instructions.map((step, idx) => (
                            <li key={idx} className="flex gap-3">
                              <span className="w-6 h-6 rounded-full bg-[#4FB3D4] text-white flex items-center justify-center flex-shrink-0 text-sm font-medium">
                                {idx + 1}
                              </span>
                              <span className="text-[#1A2A3A]/70">{step}</span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              ))}
            </div>
          )}

          {/* Wellness Tips */}
          <div className="mt-16">
            <Card className="bg-gradient-to-br from-[#FFB7B2]/10 to-[#DCD6F7]/20 rounded-2xl border-0 p-8">
              <div className="max-w-3xl mx-auto text-center">
                <h2 className="font-heading text-2xl font-semibold text-[#1A2A3A] mb-4">
                  Nutrition & Healing Connection
                </h2>
                <p className="text-[#1A2A3A]/60 mb-6">
                  What we eat directly affects our emotional and physical well-being. During recovery, 
                  nourishing your body with wholesome foods helps regulate mood, boost energy, and 
                  support your brain's healing process. These recipes are designed with love and healing in mind.
                </p>
                <Link to="/dashboard">
                  <Button 
                    className="btn-primary rounded-full px-6 py-4 font-semibold"
                    data-testid="back-to-dashboard-btn"
                  >
                    Back to Dashboard
                  </Button>
                </Link>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 lg:px-12 bg-[#1A2A3A] text-white">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="font-heading text-2xl font-bold">ThryvLife</div>
            <nav className="flex gap-8">
              <Link to="/" className="text-white/70 hover:text-white transition-colors">Home</Link>
              <Link to="/calendar" className="text-white/70 hover:text-white transition-colors">Calendar</Link>
              <Link to="/bonus" className="text-white/70 hover:text-white transition-colors">Bonus</Link>
            </nav>
            <p className="text-white/50 text-sm">© 2025 ThryvLife. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default RecipesPage;
